// src/components/Notifications/NotificationsMenu.js
import React from 'react';
import {
  Box,
  Menu,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Divider,
  Button,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications } from '../../contexts/NotificationsContext';
import { THEME_COLORS } from '../../styles/theme';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'recently';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return formatDistanceToNow(date, { addSuffix: false });
};

const buildNotificationTitle = (notification, actor) => {
  const actorName = actor?.name || 'Someone';
  switch (notification.subtype) {
    case 'request':
      return `${actorName} sent a connection request`;
    case 'accepted':
      return `${actorName} accepted your connection request`;
    case 'invite':
      return `${actorName} invited you to a group`;
    case 'join_request':
      return `${actorName} requested to join your group`;
    case 'join_approved':
      return 'Your group request was approved';
    case 'join_rejected':
      return 'Your group request was declined';
    case 'invite_accepted':
      return `${actorName} accepted your group invite`;
    case 'invite_declined':
      return `${actorName} declined your group invite`;
    case 'post_like':
      return `${actorName} liked your post`;
    case 'post_comment':
      return `${actorName} commented on your post`;
    case 'post_share':
      return `${actorName} shared your post`;
    case 'join':
      return `${actorName} joined your event`;
    default:
      return notification.title || 'New notification';
  }
};

const NotificationsMenu = ({ anchorEl, onClose }) => {
  const navigate = useNavigate();
  const {
    notifications,
    actorProfiles,
    unreadMessagesCount,
    unreadNotificationsCount,
    markNotificationRead,
    markAllNotificationsRead
  } = useNotifications();

  const open = Boolean(anchorEl);

  const handleSelect = async (notification) => {
    if (notification?.id && !notification.read) {
      await markNotificationRead(notification.id);
    }
    if (notification?.link) {
      navigate(notification.link);
    }
    onClose();
  };

  const hasContent = notifications.length > 0 || unreadMessagesCount > 0;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 360, maxWidth: '90vw', borderRadius: 2 } }}
    >
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Notifications
        </Typography>
        {unreadNotificationsCount > 0 && (
          <Button size="small" onClick={() => markAllNotificationsRead()}>
            Mark all read
          </Button>
        )}
      </Box>
      <Divider />
      <List disablePadding>
        {unreadMessagesCount > 0 && (
          <>
            <ListItemButton
              onClick={() => {
                navigate('/collaboration');
                onClose();
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: THEME_COLORS.primary }}>M</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Unread messages"
                secondary={`${unreadMessagesCount} new message${unreadMessagesCount === 1 ? '' : 's'}`}
              />
              <Chip label={unreadMessagesCount} color="error" size="small" />
            </ListItemButton>
            <Divider />
          </>
        )}

        {!notifications.length && !unreadMessagesCount && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="textSecondary">
              You are all caught up.
            </Typography>
          </Box>
        )}

        {notifications.map((notification, index) => {
          const actor = actorProfiles[notification.actorId];
          return (
            <React.Fragment key={notification.id || `note-${index}`}>
              <ListItemButton
                onClick={() => handleSelect(notification)}
                sx={{
                  bgcolor: notification.read ? 'transparent' : 'rgba(39, 35, 97, 0.06)'
                }}
              >
                <ListItemAvatar>
                  <Avatar src={actor?.profileImage} sx={{ bgcolor: THEME_COLORS.primary }}>
                    {(actor?.name || 'U').charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={notification.read ? 'normal' : 'bold'}>
                      {buildNotificationTitle(notification, actor)}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="textSecondary">
                      {formatTimestamp(notification.createdAt)}
                    </Typography>
                  }
                />
              </ListItemButton>
              {index < notifications.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>

      {!hasContent && <Divider />}
    </Menu>
  );
};

export default NotificationsMenu;
